import { useEffect } from 'react'

import { MessageSequenceStatus, MessageSort } from '@api'
import * as Collapsible from '@radix-ui/react-collapsible'
import { useUnit } from 'effector-react'
import { useNavigate } from 'react-router'

import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { SortOptionsSelect } from '@/components/ui/table/components/sort-options-select.tsx'
import { $creatorId } from '@/models/creators'
import { SettingsPageTitle } from '@/modules/creator-settings/SettingsPageTitle'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption.tsx'
import {
  $WMSettings,
  getWMSettingFx,
  toggleStateFx,
  updateMinutesFx,
  updateWMSettingsFx,
} from '@/modules/creator-settings/welcome-messages/components/models.ts'
import { WelcomeMessagesTable } from '@/modules/creator-settings/welcome-messages/components/tables/welcome-messages-table.tsx'
import { WelcomeMessagesPageSkeleton } from '@/modules/creator-settings/welcome-messages/components/welcome-messages-page-skeleton'
import {
  $welcomeMessageSortOptions,
  setSortOption,
} from '@/modules/creator-settings/welcome-messages/models.ts'
import { useWelcomeMessageSettings } from '@/modules/creator-settings/welcome-messages/query/use-welcome-message-settings'
import { TimeInputSection } from '@/modules/expiring-fans/components/time-input-section'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'
import { useDebouncedAsyncProperty } from '@/utils/useDebouncedAsyncProperty.ts'

const sortOptions = {
  Default: MessageSort.DEFAULT,
  Sent_To: MessageSort.SENT_TO,
  Reads: MessageSort.READS,
  Responses: MessageSort.RESPONSES,
  PPVs_Bought: MessageSort.PPV_BOUGHT,
}

export const WelcomeMessagesPage = () => {
  const redirect = useNavigate()
  const sortOption = useUnit($welcomeMessageSortOptions)
  const updateWMSettingsIsLoading = useUnit(updateWMSettingsFx.pending)
  const getWMSettingIsLoading = useUnit(getWMSettingFx.pending)
  const { state, minutes } = useUnit($WMSettings)
  const creatorId = useUnit($creatorId)

  const { messages, deleteMessage, handleToggleChange } =
    useWelcomeMessageSettings()

  const [delayValue, setDelayValue] = useDebouncedAsyncProperty(
    minutes!,
    updateMinutesFx,
  )

  const handleRedirectToCreateMessage = () => {
    redirect('create')
  }

  useEffect(() => {
    if (
      state &&
      messages.some(
        (message) => message.sequence.status === MessageSequenceStatus.ACTIVE,
      )
    ) {
      trackFeatureUsage(TrackedFeatures.AutoWelcomeMessages, creatorId)
    }
  }, [messages, state, creatorId])

  if (getWMSettingIsLoading) {
    return <WelcomeMessagesPageSkeleton />
  }

  return (
    <Page>
      <Collapsible.Root open={state}>
        <SettingsPageTitle
          title="Welcome Messages"
          toggleName="enable-welcome-message"
          onChange={toggleStateFx}
          enabled={state!}
          description={
            <span className="flex flex-wrap">
              Automatically sends one or multiple messages to new fans
            </span>
          }
        />
        {state && (
          <Collapsible.Content className="CollapsibleContent">
            <Separator className="mt-8" />

            <TimeInputSection
              title="Set Time"
              description="The amount of minutes when the fan will be messaged, after the subscription"
              unit="Minutes"
              textClassName="max-w-[134px]"
              value={delayValue}
              onChange={setDelayValue}
              disabled={updateWMSettingsIsLoading}
            />
            <Separator />
            <SettingsOption
              title="Messages"
              description="Configure multiple messages that will be sent to your fans"
              mobileVertical
              biggerTitle
            >
              <div className="flex gap-2">
                <SortOptionsSelect
                  sortOptions={sortOptions}
                  sortOption={sortOption}
                  handleSortChange={setSortOption}
                />
                <div>
                  <Button onClick={handleRedirectToCreateMessage}>
                    <PlusIcon /> Create Message
                  </Button>
                </div>
              </div>
            </SettingsOption>
            <WelcomeMessagesTable
              messages={messages}
              handleToggleChange={handleToggleChange}
              deleteMessage={deleteMessage}
            />
          </Collapsible.Content>
        )}
      </Collapsible.Root>
    </Page>
  )
}
